/**
 * ActivityBar — VS Code-style vertical icon strip on the far left edge.
 *
 * Ported from _parked/base/views/layout/activity-bar/activity-bar.tsx
 * with minimal changes: import remapping, strip 'use client', adapt to
 * new module registry and auth hook.
 */

import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Home, Bug, HelpCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAtomValue, useSetAtom } from 'jotai'
import { getRegisteredModules, isVisibleInPanel, isBaselineModule } from '@/core/registry'
import type { ReleasePhase } from '@/core/registry'
import { cn } from '@/shared/utils/cn'
import { activatedModulesSetAtom } from '@/lib/stores/activatedModulesAtom'
import { chromeOverlayAtom } from '@/lib/stores/chromeOverlayAtoms'
import { useBridge } from '@/core/bridge/useBridge'
import { useBridgeState } from '@/core/bridge/useBridgeState'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/overlay/Tooltip'
import { UserMenu, NotificationBell } from '@/shared/components'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import gdbLogo from '@/assets/shared/icons/gdb-logo.webp'

export interface ActivityBarItem {
  id: string
  /** Lucide icon used as a fallback when `iconSrc` isn't provided. */
  icon: LucideIcon
  /** Optional module-supplied logo (webp/png). When set the button renders
   *  this image instead of the Lucide fallback — matches how ModuleCard
   *  on the dashboard picks iconSrc over icon. */
  iconSrc?: string
  label: string
  tooltip?: string
  disabled?: boolean
  color?: string
  planTier?: 'FREE' | 'SOLO' | 'INDIE' | 'STUDIO'
  /**
   * Catalog lifecycle axes (Phase 4.5) — present when the registry entry
   * came through the `registerModuleFromCatalog` merge. Items WITHOUT
   * phase data (registry-only dev modules) default to visible, matching
   * the pre-V3 gate's behavior for non-catalog entries.
   */
  releasePhase?: ReleasePhase
  archived?: boolean
}

// Tier hierarchy for gating modules -- higher index = higher tier
const TIER_RANK: Record<string, number> = {
  FREE: 0,
  SOLO: 1,
  INDIE: 2,
  STUDIO: 3,
}

/** Check if the user's plan tier meets or exceeds the required tier */
function tierMeetsRequirement(
  userTier: string | undefined,
  requiredTier: string | undefined
): boolean {
  if (!requiredTier || requiredTier === 'FREE') return true
  if (!userTier) return false
  const userRank = TIER_RANK[userTier.toUpperCase()] ?? 0
  const requiredRank = TIER_RANK[requiredTier] ?? 0
  return userRank >= requiredRank
}

// Tier requirements per module
const MODULE_TIERS: Record<string, 'FREE' | 'SOLO' | 'INDIE' | 'STUDIO'> = {
  oracle: 'FREE',
  forge: 'SOLO',
  hoard: 'SOLO',
  prism: 'SOLO',
  echo: 'STUDIO',
}

// Tooltip overrides (module label used as fallback)
const MODULE_TOOLTIPS: Record<string, string> = {
  oracle: 'Oracle - Documentation',
  forge: 'Forge - Game Data Design',
  hoard: 'Hoard - Asset Management',
  prism: 'Prism - Visual References',
  echo: 'Echo - Audio Management',
}

/**
 * Build hub modules from the live registry. Modules only appear once the
 * user has activated them (dashboard "Activate Beta" / purchase flow →
 * `activatedModulesSetAtom`). Users who haven't bought or activated a
 * module don't see its icon here. Store lives on the main header, not the
 * activity bar.
 */
function buildHubModules(): ActivityBarItem[] {
  const registered = getRegisteredModules()

  return registered.map((mod) => ({
    id: mod.id,
    icon: mod.icon,
    iconSrc: mod.iconSrc,
    label: mod.name,
    tooltip: MODULE_TOOLTIPS[mod.id] || mod.name,
    color: mod.color,
    planTier: MODULE_TIERS[mod.id] || ('FREE' as const),
    // Phase 4.5 lifecycle axes — carried through so the visibility gate
    // below can filter without a second registry lookup or fetch.
    releasePhase: mod.releasePhase,
    archived: mod.archived,
  }))
}

interface ActivityBarProps {
  activeItem?: string
  onItemClick?: (itemId: string) => void
  position?: 'left' | 'right'
  /**
   * @deprecated Visibility gating is now applied by default (see #32).
   * This prop is retained for backward compatibility but no longer changes
   * behavior — modules are always filtered by the catalog-phase gate
   * (`isVisibleInPanel`, Plan 04.5-07).
   */
  onlyActivated?: boolean
  className?: string
}

export function ActivityBar({
  activeItem,
  onItemClick,
  position = 'left',
  className,
}: ActivityBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  // Model B: visibility (phase × environment) AND ownership (hasAccess) AND
  // activation (activatedSet) AND not-baseline ALL gate the icon strip. The
  // backend-reported `environment` keys the alpha phase; `modules` carries
  // per-module entitlement.
  const { authStatus, modules: ownedModules, environment } = useAuth()
  const activatedSet = useAtomValue(activatedModulesSetAtom)
  const { sendStateMessage } = useBridge()
  const [detachedModules, setDetachedModules] = useBridgeState<string[]>(
    'ui',
    'detachedModules',
  )
  // Bug-report and Help are GLOBAL chrome actions, not module tools — they
  // open as modal overlays (ChromeOverlays) via `chromeOverlayAtom`, not the
  // right-hand Toolbox (which is module-tools-only and hidden on the
  // dashboard). One atom write opens the matching overlay.
  const setChromeOverlay = useSetAtom(chromeOverlayAtom)

  // Chrome collapse (2026-06-06): the ActivityBar lives in the same DOM as
  // the Dashboard and EmbedMain now, so it reads `activatedModulesAtom`
  // directly through Jotai. The old `ui.requestActivatedModules` /
  // `ui.moduleActivated` replay dance — needed when each chrome was its
  // own WebView with its own localStorage — is gone.

  // Build modules once (registry is populated at import time)
  const hubModules = React.useMemo(() => buildHubModules(), [])

  // Model B gate — an icon shows IFF ALL of:
  //   1. isVisibleInPanel(phase, environment) — phase × backend-reported
  //      environment (THE single panel gate, spec §2): planning/archived
  //      never, alpha in development/staging only, beta/general always.
  //   2. hasAccess — the user is entitled to the module (OWNERSHIP gate, new
  //      in Model B). An un-owned module — or a localStorage-tampered
  //      activation — no longer leaks into the strip.
  //   3. activatedSet.has(id) — the existing activation opt-in. Owned +
  //      installed modules auto-activate on the dashboard
  //      (DashboardTileCard's effect writes activatedModulesAtom), so owned
  //      modules still appear without an explicit click.
  //   4. NOT the core/baseline module — GameDevBuddy is the Home anchor, not
  //      a module icon (excluded via isBaselineModule).
  //
  // Registry-only entries with no catalog axes (releasePhase === undefined)
  // stay visible — the pre-V3 default for non-catalog registrations — but
  // STILL pass the ownership + activation + baseline gates.
  const ownedIds = React.useMemo(
    () => new Set(ownedModules.filter((e) => e.hasAccess).map((e) => e.id)),
    [ownedModules],
  )

  const filteredModules = React.useMemo(() => {
    const visible = hubModules.filter((item) => {
      if (isBaselineModule(item.id)) return false
      const phaseVisible =
        item.releasePhase === undefined
          ? true
          : isVisibleInPanel(
              { releasePhase: item.releasePhase, archived: item.archived ?? false },
              environment,
            )
      return phaseVisible && ownedIds.has(item.id) && activatedSet.has(item.id)
    })
    console.log(
      `[Shell] ActivityBar: ${visible.length} of ${hubModules.length} modules visible`,
    )
    return visible
  }, [hubModules, environment, ownedIds, activatedSet])

  // Determine active module based on current path
  const getActiveModule = React.useCallback(() => {
    if (pathname === '/' || pathname === '') return 'dashboard'
    const pathSegments = pathname.split('/').filter(Boolean)
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0]

      // Check if it's Oracle
      if (firstSegment === 'oracle') return 'oracle'

      // Check if it's any Forge path - both /forge and /gdbforge highlight the Forge icon
      if (firstSegment === 'forge' || firstSegment === 'gdbforge') return 'forge'

      // Check if it's a top-level known module
      // Phase 17 Plan 17-06 — 'store' replaced by 'modules' (the
      // canonical browse surface; /store was deleted).
      if (['hoard', 'prism', 'ai', 'echo', 'modules', 'settings'].includes(firstSegment)) {
        return firstSegment
      }
    }
    return 'oracle' // Default to oracle
  }, [pathname])

  const [selectedModule, setSelectedModule] = React.useState(activeItem || getActiveModule())
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0)

  // Update selected module when path changes
  React.useEffect(() => {
    setSelectedModule(getActiveModule())
  }, [pathname, getActiveModule])

  // Listen for preview mode changes to re-filter modules
  React.useEffect(() => {
    const handlePreviewChange = () => {
      forceUpdate()
    }

    window.addEventListener('preview-mode-changed', handlePreviewChange)
    return () => window.removeEventListener('preview-mode-changed', handlePreviewChange)
  }, [])


  const isEmbed = pathname.startsWith('/embed/')

  const handleItemClick = (itemId: string, disabled?: boolean) => {
    if (disabled) return
    setSelectedModule(itemId)
    onItemClick?.(itemId)

    if (isEmbed) {
      const modules = getRegisteredModules()
      const mod = modules.find((m) => m.id === itemId)
      const ns = mod ? (mod.bridgeNamespace ?? mod.id) : ''

      // Hybrid modules (CC, Echo): chrome collapse Phase 2 (2026-06-06)
      // — hybrid modules ALWAYS open in their own UE nomad tab, never
      // in the main GDB panel. The main panel keeps the unified SPA
      // shell, and hybrid modules get their own native Slate window
      // alongside it (column-layout territory).
      //
      // First click: push the namespace into `ui.detachedModules`.
      // Slate listens to changes in that array and calls
      // `SGDB_MainPanel::HandleDetachModule(ns)` which registers a
      // nomad tab spawner and invokes it (creates + focuses the tab).
      //
      // Subsequent click: send `ui.focusModule` so Slate brings the
      // already-open tab to the front (`HandleFocusModule`).
      if (mod?.slotType === 'hybrid') {
        const current = detachedModules ?? []
        if (!current.includes(ns)) {
          setDetachedModules([...current, ns])
        } else {
          void sendStateMessage('ui.focusModule', { namespace: ns }).catch(
            (error: unknown) => {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Bridge focus failed'
              toast.error(`Could not focus ${mod.name}: ${message}`)
            },
          )
        }
        return
      }

      // Web modules and dashboard: chrome collapse (2026-06-06) means
      // the ActivityBar and the main content share a React tree, so
      // navigate React Router directly. No bridge round-trip.
      const route = itemId === 'dashboard' ? '/embed/main' : `/embed/main/${itemId}`
      navigate(route)
      return
    }

    // Non-embed (standalone browser): standard React Router navigation.
    // Phase 17 Plan 17-06 — 'store' retired; /modules is the canonical
    // browse surface (Buy CTAs deep-link from there to /checkout/:moduleSlug).
    const routeMap: Record<string, string> = {
      dashboard: '/',
      oracle: '/oracle',
      forge: '/forge',
      hoard: '/hoard',
      prism: '/prism',
      echo: '/echo',
      ai: '/ai/settings',
      modules: '/modules',
    }
    const route = routeMap[itemId]
    if (route) navigate(route)
  }

  // Open the global Feedback (bug-report) overlay.
  const handleOpenBugReport = React.useCallback(() => {
    setChromeOverlay('bug-report')
  }, [setChromeOverlay])

  // Open the global contextual Help overlay.
  const handleOpenHelp = React.useCallback(() => {
    setChromeOverlay('help')
  }, [setChromeOverlay])

  // Logo → dashboard. In embed mode Home routes to /embed/main, otherwise '/'.
  const handleLogoClick = React.useCallback(() => {
    navigate(isEmbed ? '/embed/main' : '/')
  }, [navigate, isEmbed])

  // Gate by user tier (no isModuleVisible filter needed -- only registered modules appear)
  const userTier = authStatus?.user?.tier
  const visibleModules = filteredModules.map((item) => ({
    ...item,
    disabled: item.disabled || !tierMeetsRequirement(userTier, item.planTier),
  }))

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex h-full w-14 flex-col border-r border-ue-border-medium shrink-0',
          'bg-ue-bg-darkest',
          position === 'right' && 'border-l border-r-0',
          className
        )}
      >
        {/* Top Section */}
        <div className="flex flex-col items-center gap-1 py-2">
          {/* GDB logo — branding anchor at the very top of the bar (relocated
              from the Slate header). Distinct from nav icons; clicking it
              returns to the dashboard. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleLogoClick}
                className="flex h-10 min-w-10 items-center justify-center rounded transition-colors hover:bg-white/5 cursor-pointer"
                aria-label="GameDevBuddy - Dashboard"
              >
                <img
                  src={gdbLogo}
                  alt="GameDevBuddy"
                  className="w-6 h-6 object-contain"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="px-2 py-1 text-xs bg-ue-bg-darker border-ue-border-medium text-ue-text-primary">
              GameDevBuddy
            </TooltipContent>
          </Tooltip>

          {/* Home / GameDevBuddy — always first, always visible. Returns to the
              dashboard from inside any module (see #56). */}
          <ActivityBarButton
            item={{
              id: 'dashboard',
              icon: Home,
              label: 'Home',
              tooltip: 'GameDevBuddy - Dashboard',
            }}
            isActive={selectedModule === 'dashboard'}
            onClick={() => handleItemClick('dashboard')}
          />

          {/* Hub Modules (Forge, Prism, Echo, AI) - filtered by visibility */}
          {visibleModules.map((item) => (
            <ActivityBarButton
              key={item.id}
              item={item}
              isActive={selectedModule === item.id}
              onClick={() => handleItemClick(item.id, item.disabled)}
            />
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom cluster — global chrome relocated from the Slate header
            (notifications, help, feedback, account). VS Code-style: avatar at
            the very bottom. The bell + user menu open UP-and-RIGHT so their
            popovers extend into the content area, not off the bottom-left.
            Store icon removed 2026-06-17 — the dashboard module grid IS the
            store, so a separate browse icon was redundant. */}
        <div className="flex flex-col items-center gap-1 py-2">
          <NotificationBell placement="up-right" />

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleOpenHelp}
                className="relative flex h-10 min-w-10 items-center justify-center rounded transition-colors hover:bg-white/5 text-ue-text-secondary hover:text-ue-text-primary cursor-pointer"
                aria-label="Open contextual help"
              >
                <HelpCircle size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="px-2 py-1 text-xs bg-ue-bg-darker border-ue-border-medium text-ue-text-primary">
              Help
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleOpenBugReport}
                className="relative flex h-10 min-w-10 items-center justify-center rounded transition-colors hover:bg-white/5 text-ue-text-secondary hover:text-ue-text-primary cursor-pointer"
                aria-label="Report a bug or share feedback"
              >
                <Bug size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="px-2 py-1 text-xs bg-ue-bg-darker border-ue-border-medium text-ue-text-primary">
              Feedback
            </TooltipContent>
          </Tooltip>

          <UserMenu placement="up-right" variant="avatar" />
        </div>

      </div>
    </TooltipProvider>
  )
}

interface ActivityBarButtonProps {
  item: ActivityBarItem
  isActive: boolean
  onClick: () => void
}

function ActivityBarButton({ item, isActive, onClick }: ActivityBarButtonProps) {
  const Icon = item.icon

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          disabled={item.disabled}
          className={cn(
            'relative flex h-10 min-w-10 p-2 items-center justify-center rounded transition-all cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-30',
            isActive
              ? 'bg-ue-accent-primary/20'
              : 'hover:bg-white/5'
          )}
          style={isActive ? { borderLeft: '3px solid var(--color-ue-accent-primary)', marginLeft: '-1px' } : {}}
          aria-label={item.label}
        >
          {item.iconSrc ? (
            <img
              src={item.iconSrc}
              alt=""
              className={cn(
                'w-[18px] h-[18px] object-contain',
                !isActive && 'opacity-80'
              )}
            />
          ) : (
            <Icon
              size={18}
              className={cn(
                isActive ? 'text-ue-accent-primary' : 'text-ue-text-secondary'
              )}
            />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="px-2 py-1 text-xs bg-ue-bg-darker border-ue-border-medium text-ue-text-primary">
        {item.disabled && item.planTier
          ? `${item.tooltip || item.label} (requires ${item.planTier} plan)`
          : (item.tooltip || item.label)}
      </TooltipContent>
    </Tooltip>
  )
}
